import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import './ServiceDelayWorkflowReport.css';

function currentMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const from = new Date(y, m, 1);
  const to = new Date(y, m + 1, 0);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function formatDate(v) {
  if (!v) return '—';
  const s = String(v).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function parseDateToNumber(value) {
  if (!value) return null;
  const s = String(value).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return Date.parse(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00`);
  const d = Date.parse(s);
  return Number.isNaN(d) ? null : d;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

function SectionGrid({ sectionKey, title, rows, columns, editableMode, onEditRow, onInlineEdit, onPrintSection, onExportSectionCsv }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState([]);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [draftFilters, setDraftFilters] = useState({});
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 280 });
  const filterButtonRefs = useRef({});

  const facets = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      if (col.filter?.kind === 'multiSelect') {
        map[col.key] = Array.from(
          new Set((rows || []).map((r) => (r[col.key] == null ? '' : String(r[col.key]).trim())).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
      }
    });
    return map;
  }, [rows, columns]);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((row) => {
      for (const col of columns) {
        const filter = filters[col.key];
        if (!filter) continue;
        const raw = row[col.key];
        if (col.filter?.kind === 'textContains') {
          const text = String(filter.text || '').trim().toLowerCase();
          if (text && !String(raw || '').toLowerCase().includes(text)) return false;
        } else if (col.filter?.kind === 'multiSelect') {
          const values = Array.isArray(filter.values) ? filter.values : [];
          if (values.length > 0 && !values.includes(String(raw || '').trim())) return false;
        } else if (col.filter?.kind === 'dateRange') {
          const n = parseDateToNumber(raw);
          if (filter.from) {
            const fromN = parseDateToNumber(filter.from);
            if (fromN != null && (n == null || n < fromN)) return false;
          }
          if (filter.to) {
            const toN = parseDateToNumber(filter.to);
            if (toN != null && (n == null || n > toN)) return false;
          }
        }
      }
      return true;
    });
  }, [rows, filters, columns]);

  const displayedRows = useMemo(() => {
    if (!Array.isArray(sort) || !sort[0]?.key) return filteredRows;
    const key = sort[0].key;
    const dir = sort[0].dir === 'desc' ? -1 : 1;
    const col = columns.find((c) => c.key === key);
    return [...filteredRows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (col?.type === 'date') return ((parseDateToNumber(av) || 0) - (parseDateToNumber(bv) || 0)) * dir;
      if (col?.type === 'number') return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
  }, [filteredRows, sort, columns]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target?.closest?.('.sdwf-header-filter-wrap')) return;
      if (target?.closest?.('.sdwf-filter-popover-portal')) return;
      setOpenFilterKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenFilterKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const updatePosition = () => {
      const button = filterButtonRefs.current[openFilterKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const col = columns.find((c) => c.key === openFilterKey);
      const width = col?.filter?.kind === 'dateRange' ? 230 : 300;
      const maxLeft = window.innerWidth - width - 8;
      const left = Math.max(8, Math.min(rect.right + window.scrollX - width, maxLeft));
      setPopoverPosition({ top: rect.bottom + window.scrollY + 6, left, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openFilterKey, columns]);

  const renderFilterControl = (column) => {
    const kind = column.filter?.kind;
    const value = draftFilters[column.key] || {};
    if (kind === 'textContains') {
      return <input className="sdwf-filter-input" type="text" value={value.text || ''} onChange={(e) => setDraftFilters((p) => ({ ...p, [column.key]: { text: e.target.value } }))} />;
    }
    if (kind === 'dateRange') {
      return (
        <div className="sdwf-filter-date-range">
          <label><span>From</span><input className="sdwf-filter-input" type="date" value={value.from || ''} onChange={(e) => setDraftFilters((p) => ({ ...p, [column.key]: { ...value, from: e.target.value } }))} /></label>
          <label><span>To</span><input className="sdwf-filter-input" type="date" value={value.to || ''} onChange={(e) => setDraftFilters((p) => ({ ...p, [column.key]: { ...value, to: e.target.value } }))} /></label>
        </div>
      );
    }
    if (kind === 'multiSelect') {
      const opts = facets[column.key] || [];
      return (
        <select multiple className="sdwf-filter-select" value={Array.isArray(value.values) ? value.values : []} onChange={(e) => setDraftFilters((p) => ({ ...p, [column.key]: { values: Array.from(e.target.selectedOptions).map((o) => o.value) } }))}>
          {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }
    return null;
  };

  const applyFilter = (column) => {
    const key = column.key;
    const draft = draftFilters[key] || {};
    const next = { ...filters };
    if (column.filter?.kind === 'textContains') {
      const text = String(draft.text || '').trim();
      if (text) next[key] = { text };
      else delete next[key];
    } else if (column.filter?.kind === 'dateRange') {
      if (draft.from || draft.to) next[key] = { from: draft.from || '', to: draft.to || '' };
      else delete next[key];
    } else if (column.filter?.kind === 'multiSelect') {
      const values = Array.isArray(draft.values) ? draft.values.filter(Boolean) : [];
      if (values.length > 0) next[key] = { values };
      else delete next[key];
    }
    setFilters(next);
    setOpenFilterKey(null);
  };

  const clearFilter = (column) => {
    const next = { ...filters };
    delete next[column.key];
    setFilters(next);
    setOpenFilterKey(null);
  };

  const openColumn = columns.find((c) => c.key === openFilterKey);
  const filterPopover = openColumn
    ? createPortal(
        <div className="sdwf-filter-popover sdwf-filter-popover-portal" style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}>
          {renderFilterControl(openColumn)}
          <div className="sdwf-filter-actions">
            <button type="button" className="btn btn-outline" onClick={() => clearFilter(openColumn)}>Clear</button>
            <button type="button" className="btn btn-primary" onClick={() => applyFilter(openColumn)}>Apply</button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <section className="sdwf-section">
      <div className="sdwf-section-header">
        <h4>{title}</h4>
        <div className="sdwf-section-actions no-print">
          <span className="sdwf-count">{displayedRows.length} rows</span>
          <button type="button" className="btn btn-secondary" onClick={() => onExportSectionCsv(sectionKey)}>Export CSV</button>
          <button type="button" className="btn btn-primary" onClick={() => onPrintSection(sectionKey)}>Print section</button>
        </div>
      </div>
      <div className="sdwf-table-wrap">
        <table className="sdwf-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} onClick={() => col.sortable !== false && setSort((prev) => {
                  const cur = Array.isArray(prev) && prev[0]?.key === col.key ? prev[0] : null;
                  if (!cur) return [{ key: col.key, dir: 'asc' }];
                  if (cur.dir === 'asc') return [{ key: col.key, dir: 'desc' }];
                  return [];
                })} className={col.sortable !== false ? 'sdwf-sortable' : ''}>
                  <div className="sdwf-header-cell">
                    <span>{col.label}{sort[0]?.key === col.key ? (sort[0].dir === 'desc' ? ' ↓' : ' ↑') : ''}</span>
                    {col.filter && (
                      <div className="sdwf-header-filter-wrap no-print" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="sdwf-filter-trigger" onClick={(e) => {
                          setDraftFilters((p) => ({ ...p, [col.key]: filters[col.key] || {} }));
                          filterButtonRefs.current[col.key] = e.currentTarget;
                          setOpenFilterKey((prev) => (prev === col.key ? null : col.key));
                        }}>
                          <svg className="sdwf-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {editableMode && <th className="no-print">Action</th>}
            </tr>
          </thead>
          <tbody>
            {displayedRows.length === 0 ? (
              <tr><td colSpan={columns.length + (editableMode ? 1 : 0)}>No rows.</td></tr>
            ) : (
              displayedRows.map((row) => (
                <tr key={`${sectionKey}-${row.id}`}>
                  {columns.map((col) => (
                    <td key={`${row.id}-${col.key}`}>
                      {col.editable && col.type === 'date' ? (
                        <input
                          key={row[col.key] || ''}
                          type="date"
                          className="sdwf-inline-date"
                          defaultValue={row[col.key] ? String(row[col.key]).slice(0, 10) : ''}
                          onBlur={(e) => {
                            const newVal = e.target.value;
                            const oldVal = row[col.key] ? String(row[col.key]).slice(0, 10) : '';
                            if (newVal !== oldVal && onInlineEdit) {
                              onInlineEdit(sectionKey, row.id, col.key, newVal || null);
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      ) : col.type === 'date' ? formatDate(row[col.key]) : (row[col.key] == null || row[col.key] === '' ? '—' : row[col.key])}
                    </td>
                  ))}
                  {editableMode && (
                    <td className="no-print">
                      <button type="button" className="btn btn-outline" onClick={() => onEditRow(sectionKey, row)}>Edit</button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {filterPopover}
    </section>
  );
}

const SECTION_DEFS = {
  new_delays: {
    title: 'New delays',
    editableMode: false,
    columns: [
      { key: 'ei_number', label: 'EI#', filter: { kind: 'textContains' } },
      { key: 'staff_initials', label: 'SC Initials', filter: { kind: 'multiSelect' } },
      { key: 'client_last_name', label: 'Last Name', filter: { kind: 'textContains' } },
      { key: 'client_first_name', label: 'First Name', filter: { kind: 'textContains' } },
      { key: 'client_dob', label: 'DOB', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_start_date', label: 'Delay Start', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_reason_code', label: 'Delay Reason', filter: { kind: 'multiSelect' } },
      { key: 'service_code', label: 'Service Code', filter: { kind: 'multiSelect' } },
      { key: 'monthly_amount', label: 'Monthly Amount', type: 'number', filter: { kind: 'multiSelect' } },
      { key: 'zip', label: 'Zip', filter: { kind: 'textContains' } },
      { key: 'county_cs_code', label: 'CS Code', type: 'number', filter: { kind: 'multiSelect' } },
      { key: 'ifsp_date', label: 'IFSP Date', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_comment', label: 'Delay Explanation', sortable: false, filter: { kind: 'textContains' } },
    ],
  },
  resolved_delays: {
    title: 'Resolved delays (not DHS reported)',
    editableMode: false,
    columns: [
      { key: 'staff_name_fl', label: 'SC', filter: { kind: 'multiSelect' } },
      { key: 'client_name', label: 'Client', filter: { kind: 'textContains' } },
      { key: 'delay_start_date', label: 'Start', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_end_date', label: 'Resolved', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_reason_code', label: 'Reason', filter: { kind: 'multiSelect' } },
      { key: 'service_code', label: 'Service Code', filter: { kind: 'multiSelect' } },
      { key: 'delay_comment', label: 'Explanation', sortable: false, filter: { kind: 'textContains' } },
    ],
  },
  resolved_dhs_edit: {
    title: 'Resolved delay DHS reporting queue',
    editableMode: true,
    columns: [
      { key: 'client_name', label: 'Client', filter: { kind: 'textContains' } },
      { key: 'delay_start_date', label: 'Start', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_end_date', label: 'Resolved', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_reported_to_dhs_date', label: 'DHS reported', type: 'date', editable: true, filter: { kind: 'dateRange' } },
      { key: 'delay_comment', label: 'Explanation', sortable: false, filter: { kind: 'textContains' } },
    ],
  },
  closed_not_resolved: {
    title: 'Client closed, delay not resolved',
    editableMode: true,
    columns: [
      { key: 'client_name', label: 'Client', filter: { kind: 'textContains' } },
      { key: 'referral_close_date', label: 'Closed', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_start_date', label: 'Delay start', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_end_date', label: 'Delay resolved', type: 'date', filter: { kind: 'dateRange' } },
      { key: 'delay_comment', label: 'Explanation', sortable: false, filter: { kind: 'textContains' } },
    ],
  },
};

function ServiceDelayWorkflowReport({ refreshKey = 0, tenantCode = null }) {
  const range = currentMonthRange();
  const [startDate, setStartDate] = useState(range.from);
  const [endDate, setEndDate] = useState(range.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [runFeedback, setRunFeedback] = useState(null);
  const [printScope, setPrintScope] = useState('all');
  const sectionRefs = useRef({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setMessage(null);
    setRunFeedback('Running report…');
    return reportsAPI.get('service-delay-workflow', { start_date: startDate, end_date: endDate })
      .then((res) => {
        setData(res.data);
        const ranAt = new Date();
        const summary = res?.data?.summary || {};
        const totalRows = (summary.new_delays || 0)
          + (summary.resolved_delays || 0)
          + (summary.resolved_dhs_edit || 0)
          + (summary.closed_not_resolved || 0);
        setRunFeedback(`Run complete at ${ranAt.toLocaleTimeString()} (${totalRows} rows across sections)`);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load service delay workflow');
        setRunFeedback('Run failed.');
        setLoading(false);
      });
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    const onAfterPrint = () => setPrintScope('all');
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  const sections = useMemo(() => {
    if (!data) return [];
    return Object.keys(SECTION_DEFS).map((key) => ({
      key,
      title: SECTION_DEFS[key].title,
      columns: SECTION_DEFS[key].columns,
      editableMode: SECTION_DEFS[key].editableMode,
      rows: data[key] || [],
    }));
  }, [data]);

  const handleEditRow = async (sectionKey, row) => {
    try {
      if (sectionKey === 'resolved_dhs_edit') {
        const nextReported = window.prompt('DHS reported date (YYYY-MM-DD). Leave empty to clear.', row.delay_reported_to_dhs_date || '');
        if (nextReported == null) return;
        const nextComment = window.prompt('Delay explanation/comment', row.delay_comment || '');
        if (nextComment == null) return;
        await reportsAPI.serviceDelayUpdateRow(row.id, { delay_reported_to_dhs_date: nextReported || null, delay_comment: nextComment || null });
      } else if (sectionKey === 'closed_not_resolved') {
        const nextResolved = window.prompt('Delay resolved date (YYYY-MM-DD). Leave empty to clear.', row.delay_end_date || '');
        if (nextResolved == null) return;
        const nextComment = window.prompt('Delay explanation/comment', row.delay_comment || '');
        if (nextComment == null) return;
        await reportsAPI.serviceDelayUpdateRow(row.id, { delay_end_date: nextResolved || null, delay_comment: nextComment || null });
      }
      setMessage('Row updated.');
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update row');
    }
  };

  const handleInlineEdit = async (sectionKey, rowId, fieldKey, value) => {
    try {
      await reportsAPI.serviceDelayUpdateRow(rowId, { [fieldKey]: value });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update row');
    }
  };

  const handleAutoResolve = async () => {
    try {
      const ok = window.confirm('Auto-resolve open delays for clients who reached age 3?');
      if (!ok) return;
      const res = await reportsAPI.serviceDelayAutoResolve();
      setMessage(`Auto-resolve complete. Updated ${res.data?.updated || 0} row(s).`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed auto-resolve action');
    }
  };

  const handleExportSectionCsv = async (sectionKey) => {
    try {
      const res = await reportsAPI.serviceDelayExportCsv({ section: sectionKey, start_date: startDate, end_date: endDate });
      downloadBlob(res.data, `service_delay_${sectionKey}.csv`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to export CSV');
    }
  };

  const handlePrintSection = (sectionKey) => {
    setPrintScope(sectionKey);
    setTimeout(() => window.print(), 0);
  };

  return (
    <div className="sdwf-report">
      <div className="sdwf-toolbar no-print">
        <h3 className="sdwf-title">Service Delay Workflow{tenantCode ? ` - ${tenantCode}` : ''}</h3>
        <div className="sdwf-toolbar-actions">
          <label>From <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
          <label>To <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
          <button type="button" className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? 'Running…' : 'Run'}
          </button>
          <button type="button" className="btn btn-secondary sdwf-toolbar-btn" onClick={handleAutoResolve}>Auto resolve aged-out</button>
          <button type="button" className="btn btn-secondary sdwf-toolbar-btn" onClick={() => setPrintScope('all') || setTimeout(() => window.print(), 0)}>Print all</button>
          {loading ? <span className="sdwf-loading">Refreshing…</span> : null}
        </div>
      </div>
      {runFeedback && <div className="sdwf-message no-print">{runFeedback}</div>}

      {data && (
        <p className="sdwf-summary">
          Filters: Date range {formatDate(startDate)} to {formatDate(endDate)} |
          {' '}Total delay rows: {data.summary?.total_delay_rows || 0} |
          {' '}New: {data.summary?.new_delays || 0} |
          {' '}Resolved: {data.summary?.resolved_delays || 0} |
          {' '}DHS edit: {data.summary?.resolved_dhs_edit || 0} |
          {' '}Closed not resolved: {data.summary?.closed_not_resolved || 0}
        </p>
      )}

      {message && <div className="sdwf-message no-print">{message}</div>}
      {error && <div className="sdwf-error">{error}</div>}
      {loading && !data && <div className="sdwf-loading">Loading workflow…</div>}

      {data && sections.map((section) => (
        <div
          key={section.key}
          ref={(el) => { sectionRefs.current[section.key] = el; }}
          className={printScope !== 'all' && printScope !== section.key ? 'sdwf-print-hidden' : ''}
        >
          <SectionGrid
            sectionKey={section.key}
            title={section.title}
            columns={section.columns}
            rows={section.rows}
            editableMode={section.editableMode}
            onEditRow={handleEditRow}
            onInlineEdit={handleInlineEdit}
            onPrintSection={handlePrintSection}
            onExportSectionCsv={handleExportSectionCsv}
          />
        </div>
      ))}
    </div>
  );
}

export default ServiceDelayWorkflowReport;
